import type {
  DashboardActivityAlertLevel,
  DashboardActivityRow,
  DashboardActivityTableSortColumn,
  DashboardActivityTableSortDirection,
} from '../../interfaces/dashboardLeaderboardActivity';

/** Seed context for mock rows (no date-range params). */
export type ActivityTableMockContext = {
  teamId?: string | null;
  playersGroup?: string;
};

function makeSeededRand(seedStr: string) {
  let h = 0;
  for (let i = 0; i < seedStr.length; i += 1) h = (Math.imul(31, h) + seedStr.charCodeAt(i)) | 0;
  return (n: number) => {
    h = (Math.imul(1103515245, h) + 12345) | 0;
    return Math.abs(h) % n;
  };
}

/** Deterministic mock row for global index `idx` (stable across pages). */
export function createMockActivityRow(idx: number, ctx: ActivityTableMockContext): DashboardActivityRow {
  const rand = makeSeededRand(JSON.stringify(ctx) + String(idx));
  const nullish = rand(6) === 0;

  const alertLevels: DashboardActivityAlertLevel[] = [null, 'no alerts', 'watch', 'medical', 'warning'];
  const pickAlert = (): DashboardActivityAlertLevel => alertLevels[rand(alertLevels.length)];
  const postTypes: (string | null)[] = [null, 'Game', 'Velo day', 'Bullpen', 'Pitching'];

  const showcaseNames = [
    'Bryce Burton',
    'Chris Cicero',
    'Dane Danielson',
    'Marcus Rivera',
    'Jake Thompson',
    'Tyler Brooks',
    'Ryan Mitchell',
    'Kevin Anderson',
  ];
  const name = idx < showcaseNames.length ? showcaseNames[idx] : `Player ${idx + 1}`;

  return {
    playerId: `act-player-${idx}`,
    playerName: name,
    dailyCheckIn: nullish
      ? null
      : {
          date: new Date(Date.now() - rand(3) * 86400000).toISOString(),
          armFeels: rand(8) === 0 ? null : 1 + rand(10),
          bodyFeels: rand(8) === 0 ? null : 1 + rand(10),
          sleepQuality: rand(8) === 0 ? null : 1 + rand(10),
        },
    freshAlerts: nullish
      ? null
      : {
          lastFreshDate: new Date(Date.now() - rand(14) * 86400000).toISOString(),
          strength: pickAlert(),
          recovery: pickAlert(),
        },
    postAlerts: nullish
      ? null
      : {
          lastPostDate: new Date(Date.now() - rand(20) * 86400000).toISOString(),
          fatigue: pickAlert(),
          activityType: postTypes[rand(postTypes.length)],
          rpe: rand(5) === 0 ? null : 1 + rand(10),
          count: rand(5) === 0 ? null : 1 + rand(110),
        },
    trainingSessions: nullish ? null : 1 + rand(8),
  };
}

function rankAlert(level: DashboardActivityAlertLevel): number {
  if (level == null) return -1;
  const order: DashboardActivityAlertLevel[] = ['no alerts', 'watch', 'warning', 'medical'];
  const i = order.indexOf(level);
  return i >= 0 ? i : 0;
}

function cmpNum(
  a: number | null | undefined,
  b: number | null | undefined,
  desc: boolean,
): number {
  const aN = a == null ? null : a;
  const bN = b == null ? null : b;
  if (aN == null && bN == null) return 0;
  if (aN == null) return 1;
  if (bN == null) return -1;
  const c = aN - bN;
  return desc ? -c : c;
}

function cmpStr(a: string | null | undefined, b: string | null | undefined, desc: boolean): number {
  const as = a ?? '';
  const bs = b ?? '';
  const c = as.localeCompare(bs, undefined, { numeric: true, sensitivity: 'base' });
  return desc ? -c : c;
}

function cmpIsoDate(a: string | null | undefined, b: string | null | undefined, desc: boolean): number {
  const ta = a ? Date.parse(a) : NaN;
  const tb = b ? Date.parse(b) : NaN;
  const aOk = !Number.isNaN(ta);
  const bOk = !Number.isNaN(tb);
  if (!aOk && !bOk) return 0;
  if (!aOk) return 1;
  if (!bOk) return -1;
  const c = ta - tb;
  return desc ? -c : c;
}

export function sortDashboardActivityRows(
  rows: DashboardActivityRow[],
  column: DashboardActivityTableSortColumn,
  direction: DashboardActivityTableSortDirection,
): DashboardActivityRow[] {
  const desc = direction === 'desc';
  const sorted = [...rows];
  sorted.sort((ra, rb) => {
    const c = ra.dailyCheckIn;
    const f = ra.freshAlerts;
    const p = ra.postAlerts;
    const c2 = rb.dailyCheckIn;
    const f2 = rb.freshAlerts;
    const p2 = rb.postAlerts;
    let out = 0;
    switch (column) {
      case 'playerName':
        out = cmpStr(ra.playerName, rb.playerName, desc);
        break;
      case 'dailyDate':
        out = cmpIsoDate(c?.date, c2?.date, desc);
        break;
      case 'armFeels':
        out = cmpNum(c?.armFeels, c2?.armFeels, desc);
        break;
      case 'bodyFeels':
        out = cmpNum(c?.bodyFeels, c2?.bodyFeels, desc);
        break;
      case 'sleepQuality':
        out = cmpNum(c?.sleepQuality, c2?.sleepQuality, desc);
        break;
      case 'freshDate':
        out = cmpIsoDate(f?.lastFreshDate, f2?.lastFreshDate, desc);
        break;
      case 'strength':
        out = cmpNum(rankAlert(f?.strength ?? null), rankAlert(f2?.strength ?? null), desc);
        break;
      case 'recovery':
        out = cmpNum(rankAlert(f?.recovery ?? null), rankAlert(f2?.recovery ?? null), desc);
        break;
      case 'postDate':
        out = cmpIsoDate(p?.lastPostDate, p2?.lastPostDate, desc);
        break;
      case 'fatigue':
        out = cmpNum(rankAlert(p?.fatigue ?? null), rankAlert(p2?.fatigue ?? null), desc);
        break;
      case 'type':
        out = cmpStr(p?.activityType ?? '', p2?.activityType ?? '', desc);
        break;
      case 'rpe':
        out = cmpNum(p?.rpe, p2?.rpe, desc);
        break;
      case 'count':
        out = cmpNum(p?.count, p2?.count, desc);
        break;
      case 'trainingSessions':
        out = cmpNum(ra.trainingSessions, rb.trainingSessions, desc);
        break;
      default:
        out = 0;
    }
    if (out !== 0) return out;
    return cmpStr(ra.playerId, rb.playerId, false);
  });
  return sorted;
}
